import { clearLayoutAuthCache } from '@/lib/layout-auth-cache';
import { clearAppLanguageStorage } from '@/lib/i18n/language-storage';

/** Profile / tenant scope only — never store access or refresh tokens here. */
const USER_KEY = 'user';
const TENANT_ID_KEY = 'tenantId';
const SUBSCRIPTION_ERROR_KEY = 'subscription_error';

/** Dashboard attention strip dismiss — session only; cleared on logout. */
export const ATTENTION_BANNER_DISMISS_KEY = 'dashboard.attentionBanner.dismissed';

/** Dashboard all-clear (green) strip dismiss — session only; cleared on logout. */
export const ALL_CLEAR_BANNER_DISMISS_KEY = 'dashboard.allClearBanner.dismissed';

/** Legacy keys cleared on logout / session persist (migration from localStorage tokens). */
const LEGACY_AUTH_TOKEN_KEY = 'auth_token';
const LEGACY_REFRESH_TOKEN_KEY = 'refresh_token';

/**
 * Platform / SuperAdmin JWT claim — not a real firm tenant.
 * Must never be used as X-Tenant-ID operational scope.
 */
export function isSystemTenantId(tenantId: string | null | undefined): boolean {
  return !!tenantId?.trim() && tenantId.trim().toLowerCase() === 'system';
}

/** Access JWT lives in memory only (XSS cannot exfiltrate via localStorage). */
let accessTokenMemory: string | null = null;
let authSessionEnded = false;

type AuthSessionListener = () => void;
const authSessionListeners = new Set<AuthSessionListener>();

/** Same-tab login/logout/refresh — `storage` events do not fire in the writer tab. */
export function subscribeAuthSession(listener: AuthSessionListener): () => void {
  authSessionListeners.add(listener);
  return () => {
    authSessionListeners.delete(listener);
  };
}

function notifyAuthSessionListeners(): void {
  authSessionListeners.forEach((listener) => listener());
}

export function getAccessToken(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return accessTokenMemory;
}

export function setAccessToken(token: string | null): void {
  accessTokenMemory = token?.trim() ? token : null;
}

/** True after logout or when there is no in-memory access token and no restoreable user hint. */
export function isAuthSessionEnded(): boolean {
  if (typeof window === 'undefined') {
    return true;
  }
  if (authSessionEnded) {
    return true;
  }
  // During restore, user may exist without access token yet — not ended.
  return false;
}

export function hasStoredUser(): boolean {
  if (typeof window === 'undefined') {
    return false;
  }
  return !!localStorage.getItem(USER_KEY);
}

/**
 * Remove all auth/session keys so a prior SuperAdmin tenant scope cannot leak to TenantAdmin.
 */
export function clearAuthStorage(): void {
  if (typeof window === 'undefined') {
    return;
  }

  authSessionEnded = true;
  accessTokenMemory = null;

  localStorage.removeItem(LEGACY_AUTH_TOKEN_KEY);
  localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
  localStorage.removeItem(TENANT_ID_KEY);
  localStorage.removeItem(SUBSCRIPTION_ERROR_KEY);

  document.cookie = 'auth_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
  try {
    sessionStorage.removeItem(ATTENTION_BANNER_DISMISS_KEY);
    sessionStorage.removeItem(ALL_CLEAR_BANNER_DISMISS_KEY);
  } catch {
    // ignore quota / private mode
  }
  clearLayoutAuthCache();
  clearAppLanguageStorage();
  notifyAuthSessionListeners();
}

export interface PersistedAuthSession {
  token: string;
  /** @deprecated Refresh is HttpOnly cookie; ignored if present. */
  refreshToken?: string;
  user: unknown;
  tenantId?: string | null;
}

/**
 * Store session after login/refresh. Access JWT in memory; profile in localStorage; refresh via cookie.
 */
export function persistAuthSession(session: PersistedAuthSession): void {
  if (typeof window === 'undefined') {
    return;
  }

  authSessionEnded = false;
  setAccessToken(session.token);

  // Purge any legacy token copies from older clients.
  localStorage.removeItem(LEGACY_AUTH_TOKEN_KEY);
  localStorage.removeItem(LEGACY_REFRESH_TOKEN_KEY);

  localStorage.setItem(USER_KEY, JSON.stringify(session.user));

  const role = readRoleFromUser(session.user);
  const tenantId = session.tenantId?.trim() || readTenantIdFromUser(session.user);

  if (role === 'SuperAdmin') {
    // SuperAdmin JWT tenant is "system" — that is not an operational firm scope.
    // Do not write it (would mount Admin dashboard → 401). Do not clear an existing
    // SuperAdmin-selected firm on token refresh either.
    if (tenantId && !isSystemTenantId(tenantId)) {
      localStorage.setItem(TENANT_ID_KEY, tenantId);
    }
  } else if (tenantId && !isSystemTenantId(tenantId)) {
    localStorage.setItem(TENANT_ID_KEY, tenantId);
  } else {
    localStorage.removeItem(TENANT_ID_KEY);
  }

  notifyAuthSessionListeners();
}

function readRoleFromUser(user: unknown): string | undefined {
  if (!user || typeof user !== 'object' || !('role' in user)) {
    return undefined;
  }
  const role = (user as { role?: unknown }).role;
  return typeof role === 'string' ? role : undefined;
}

function readTenantIdFromUser(user: unknown): string | undefined {
  if (!user || typeof user !== 'object' || !('tenantId' in user)) {
    return undefined;
  }
  const tenantId = (user as { tenantId?: unknown }).tenantId;
  return typeof tenantId === 'string' ? tenantId.trim() : undefined;
}

export function getStoredTenantId(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  const tenantId = localStorage.getItem(TENANT_ID_KEY)?.trim() || null;
  if (!tenantId) {
    return null;
  }
  // Self-heal stale SuperAdmin sessions that persisted JWT "system".
  if (isSystemTenantId(tenantId)) {
    localStorage.removeItem(TENANT_ID_KEY);
    return null;
  }
  return tenantId;
}

/** Runtime tenant scope for SuperAdmin global selector only. */
export function setStoredTenantId(tenantId: string): void {
  if (typeof window === 'undefined') {
    return;
  }
  const trimmed = tenantId.trim();
  if (!trimmed || isSystemTenantId(trimmed)) {
    return;
  }
  localStorage.setItem(TENANT_ID_KEY, trimmed);
}

/** Clears SuperAdmin operational tenant scope ("all tenants" mode). */
export function clearStoredTenantId(): void {
  if (typeof window === 'undefined') {
    return;
  }
  localStorage.removeItem(TENANT_ID_KEY);
}

export function getStoredUserJson(): string | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return localStorage.getItem(USER_KEY);
}
